import { useState, useMemo } from 'react';
import { taskApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Button, ErrorAlert, SkeletonCard, EmptyState } from '../../components/ui';
import { TaskListCard } from '../../components/tasks';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

export function TaskListPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'available' | 'all'>('available');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['tasks', filter, page],
    queryFn: () => taskApi.list(page, 20, filter),
    refetchOnMount: 'always',
  });

  const filteredTasks = useMemo(() => {
    const tasks = !searchQuery.trim()
      ? data?.tasks || []
      : (data?.tasks || []).filter(t =>
          t.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    return [...tasks].sort((a, b) => {
      const order = (t: typeof a) => {
        const isActive = t.status === 'active' && new Date(t.end_date) >= new Date();
        if (!isActive) return 4; // completed/expired last
        const s = t.user_participation_status;
        if (s === 'claimed') return 0;
        if (s === 'submitted') return 1;
        if (s === 'approved') return 3;
        return 2; // no participation or rejected
      };
      return order(a) - order(b);
    });
  }, [data?.tasks, searchQuery]);

  const filters = [
    { value: 'all' as const, label: 'All Tasks' },
    { value: 'available' as const, label: 'Available' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Available Tasks</h1>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 pr-4 py-2.5 w-60 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setFilter(f.value);
              setPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
              filter === f.value
                ? 'bg-primary-600 text-white shadow-sm'
                : 'border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : isError ? (
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          icon={Search}
          title={searchQuery ? 'No matching tasks' : 'No tasks available'}
          description={searchQuery ? `No tasks found for "${searchQuery}".` : 'There are no tasks available at the moment. Check back later.'}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTasks.map((task, index) => (
              <div key={task.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                <TaskListCard task={task} />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * 20 + 1} to{' '}
                {Math.min(page * 20, data.pagination.total)} of{' '}
                {data.pagination.total} tasks
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
